import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const error = location.state?.error;

  useEffect(() => {
    // If already authenticated, jump to dashboard.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => setChecking(false));
  }, [navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="mx-auto w-full max-w-6xl px-5 md:px-8 pt-7">
        <a
          href="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-burgundy/70 hover:text-burgundy transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to guides
        </a>
      </header>

      <main className="flex-1 grid place-items-center px-5 md:px-8 py-16">
        <div className="w-full max-w-md rounded-2xl bg-white border border-sand shadow-editorial p-8 md:p-10">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-burgundy">
            <Lock className="h-3.5 w-3.5" /> Admin only
          </div>
          <h1 className="mt-4 font-heading text-3xl md:text-4xl text-burgundy-ink leading-tight">
            Sign in to the studio.
          </h1>
          <p className="mt-3 text-foreground/70">
            Manage your guides and see who's downloading what. Sign in with your Google account below.
          </p>

          {checking ? (
            <div className="mt-8 flex items-center gap-2 text-foreground/60">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking session…
            </div>
          ) : (
            <Button
              data-testid="google-signin-btn"
              onClick={signIn}
              className="mt-8 w-full h-12 rounded-xl bg-burgundy hover:bg-burgundy-700 text-white text-[15px] font-medium"
            >
              Continue with Google
            </Button>
          )}

          {error && (
            <p data-testid="login-error" className="mt-4 text-sm text-red-700">{error}</p>
          )}

          <p className="mt-6 text-xs text-foreground/50 leading-relaxed">
            Only authorized emails can access the admin panel. If you're not the owner, this page will politely turn you away.
          </p>
        </div>
      </main>
    </div>
  );
}
